import {useState, useEffect} from 'react';

import type {SupportedNetwork} from 'src/types';
import {getStore, setValue} from 'src/v2/state/store';
import {WalletCard} from 'src/v2/components/wallet/WalletCard';
import {TokenCard} from 'src/v2/components/pay/TokenCard';
import {ConnectedWalletCard} from 'src/v2/components/pay/ConnectedWalletCard';
import {BrandedButton, LinkButton} from 'src/v2/components/common';
import {CurrencySelector} from 'src/v2/components/pay/CurrencySelector';
import {WalletActionMenu} from 'src/v2/components/pay/WalletActionMenu';
import type {SupportedCurrency, Wallet} from 'src/v2/state/types';
import {usePaymentPolling} from 'src/v2/components/pay/usePaymentPolling';
import {
  formatCurrencyAmountToMajorUnits,
  formatCurrencyAmountToMajorUnitsFixed,
  formatAmountToDollars,
} from 'src/utils/helpers';
import {useHandlePayment} from 'src/v2/components/pay/useHandlePayment';
import {PaymentConfirmationPendingOverlay} from 'src/v2/components/pay/PaymentConfirmationPendingOverlay';
import {FutureUsageConfirmationPendingOverlay} from 'src/v2/components/pay/FutureUsageConfirmationPendingOverlay';
import {getExplorerUrl} from 'src/v2/util/getExplorerUrl';
import {SetupTerms} from 'src/v2/components/setup/SetupTerms';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {SmartWalletBanner} from 'src/v2/components/pay/SmartWalletBanner';
import {WalletRegistry} from 'src/v2/util/WalletRegistry';
import {useNetworkSwitchingEffect} from 'src/v2/state/useNetworkSwitching';
import {useStartPollingOnPageLoad} from 'src/v2/components/pay/useStartPollingOnPageLoad';
import {Banner} from 'src/v2/components/common/Banner';
import {
  type CurrencyBalance,
  useCurrencyBalances,
} from 'src/v2/state/useCurrencyBalances';
import {AnimatePresence, motion} from '@sail/motion';
import {TransactionProcessingTimeDisclaimer} from 'src/v2/components/pay/TransactionProcessingTimeDisclaimer';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';
import {
  useRelayQuoteForPayment,
  QUOTE_REFRESH_INTERVAL_S,
} from 'src/v2/components/pay/useRelayQuoteForPayment';
import {useIsRelayWalletConnectCurrency} from 'src/v2/components/pay/useIsRelayWalletConnectCurrency';
import {useHandleRelayPayment} from 'src/v2/components/pay/useHandleRelayPayment';
import {QuoteErrorScreen} from 'src/v2/components/anyCrypto/QuoteErrorScreen';
import {clearErrorBanner} from 'src/v2/components/error/handleError';

import {message, useIntl} from '@sail/intl';
import {view} from '@sail/ui';

const hasSufficientBalance = (
  {amountInUsdMinor}: CurrencyBalance,
  depositAmount: number,
  paymentCurrency: string,
  // Default to true if we can't get the balance either because it's loading or errored
) => {
  if (amountInUsdMinor === null) {
    return true;
  }
  // Convert deposit amount from source currency to USD cents (2 decimals)
  // formatCurrencyAmountToMajorUnits converts to major units, then * 100 to get cents
  const depositAmountInCents =
    formatCurrencyAmountToMajorUnits(depositAmount, paymentCurrency) * 100;

  return amountInUsdMinor >= depositAmountInCents;
};

const messages = {
  currencyLabel: message({
    id: 'crypto.payins.v2.pay_screen.currency_label',
    description: 'Label on the currency selector',
    defaultMessage: 'Currency',
  }),

  payText: message<{
    amount: React.ReactNode;
    currency: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.pay_screen.pay_text',
    description: 'Label on the button to pay',
    defaultMessage: 'Pay {amount} {currency}',
  }),

  processingText: message({
    id: 'crypto.payins.v2.pay_screen.processing_text',
    description: 'Label on the button when payment is processing',
    defaultMessage: 'Processing...',
  }),

  submittingOnChainText: message({
    id: 'crypto.payins.v2.pay_screen.submitting_on_chain_text',
    description: 'Label for button when submitting tramsactopm on chain',
    defaultMessage: 'Submitting on chain...',
  }),

  verifyingTransactionText: message({
    id: 'crypto.payins.v2.pay_screen.verifying_on_chain_text',
    description: 'Label for button when verifying transaction on chain',
    defaultMessage: 'Verifying transaction...',
  }),

  viewTransaction: message({
    id: 'crypto.payins.v2.pay_screen.view_transaction',
    description: 'Text for the link to view the transaction on the network',
    defaultMessage: 'View transaction on network',
  }),

  insufficientBalance: message({
    id: 'crypto.payins.v2.pay_screen.insufficient_balance',
    description: 'Message when the selected currency has insufficient balance',
    defaultMessage: 'Your wallet has insufficient funds.',
  }),
};

interface Props {
  activeWallet: Wallet;
}

export const PayScreen = ({activeWallet}: Props) => {
  const intl = useIntl();
  const {isPolling: isPollingPayment, startPolling: startPollingPayment} =
    usePaymentPolling();
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false); // api loading
  const [buttonMessageKey, setButtonMessageKey] = useState<
    'pay' | 'processing' | 'submitting' | 'verifying'
  >('pay');

  const handlePayment = useHandlePayment();
  const handleRelayPayment = useHandleRelayPayment();
  const store = getStore();
  const amount = store.useSelector((state) => state.paymentDetails.amount);
  const paymentCurrency = store.useSelector(
    (state) => state.paymentDetails.currency,
  );
  const showPaymentConfirmationPendingOverlay = store.useSelector(
    (state) => state.overlay === 'CONFIRM_PAYMENT',
  );
  const showFutureUsageConfirmationPendingOverlay = store.useSelector(
    (state) => state.overlay === 'CONFIRM_PAYMENT_WITH_FUTURE_USAGE',
  );
  const transactionHash = store.useSelector(
    (state) => state.transactionDetails?.blockchainTxId,
  );
  const network = store.useSelector(
    (state) => state.transactionDetails?.destinationNetwork,
  );
  const livemode = store.useSelector((state) => state.livemode);
  const hasSucceededOrProcessingSetupAttempt = store.useSelector(
    (state) => state.hasSucceededOrProcessingSetupAttempt,
  );
  const sessionState = store.useSelector((state) => state.state);
  const isSessionTerminal =
    sessionState === 'error' || sessionState === 'canceled';

  const {data: currencyBalances, loading: loadingBalances} =
    useCurrencyBalances();
  // Prefer token selected via the any crypto token select UI, if available
  const anyCryptoSelectedToken = store.useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    () => {
      if (anyCryptoSelectedToken) {
        const match = currencyBalances.find(
          (c) => c.currency.assetCode === anyCryptoSelectedToken,
        );
        if (match) return match.currency;
      }
      return currencyBalances[0]!.currency;
    },
  );

  const enableNonStablesWalletUi = store.useSelector(
    (state) =>
      state.featureFlags.enable_pay_with_any_crypto_wallet_connect_non_stables,
  );

  const {
    quote: relayQuote,
    loading: relayQuoteLoading,
    error: relayQuoteError,
    secondsUntilRefresh,
  } = useRelayQuoteForPayment();

  // Sync selected currency to global store so PayTitle can display it
  useEffect(() => {
    store.setValue((state) => {
      state.selectedCurrency = selectedCurrency;
    });
  }, [selectedCurrency, store]);

  const setupFutureUsage = store.useSelector((state) => state.setupFutureUsage);

  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();

  useNetworkSwitchingEffect();

  useStartPollingOnPageLoad({
    startPolling: startPollingPayment,
    isPolling: isPollingPayment,
  });

  const selectedCurrencyBalance = currencyBalances.find(
    (c) => c.currency.id === selectedCurrency.id,
  )!;

  const insufficientBalance = !hasSufficientBalance(
    selectedCurrencyBalance,
    amount,
    paymentCurrency,
  );

  const isRelayCurrency = useIsRelayWalletConnectCurrency();

  const onClickPay = async () => {
    clearErrorBanner();
    if (isRelayCurrency) {
      await handleRelayPayment({
        selectedCurrency: selectedCurrencyBalance.currency,
        setIsProcessing: setIsSubmittingPayment,
      });
      return;
    }
    await handlePayment({
      selectedCurrency: selectedCurrencyBalance.currency,
      setIsProcessing: setIsSubmittingPayment,
      onConfirmSuccess: startPollingPayment,
    });
  };

  const explorerUrl = transactionHash
    ? getExplorerUrl({
        network: network as SupportedNetwork,
        livemode,
        blockchainTxId: transactionHash,
      })
    : null;

  const walletOption = WalletRegistry.getWalletOption({
    provider: activeWallet.type,
    key: activeWallet.key,
  });

  if (!walletOption) {
    throw new Error('Wallet option not found in PayScreen');
  }

  const isPaymentProcessing = isSubmittingPayment || isPollingPayment;
  const isScreenInProgress =
    isPaymentProcessing || loadingBalances || relayQuoteLoading;

  useEffect(() => {
    // This order is important, as some conditions overlap.
    // Think carefully about the order of these conditions before changing it.

    if (sessionState === 'purchase_complete') {
      // The customer's txn has been submittted on-chain and we are verifying the transaction
      setButtonMessageKey('verifying');
    } else if (hasSucceededOrProcessingSetupAttempt) {
      // In the setup future usage/off-session flow, we submit the txn on-chain for the customer.
      // If we have a setup attempt, that means we are submitting the txn on-chain.
      setButtonMessageKey('submitting');
    } else if (isPaymentProcessing) {
      // When the customer has clicked the pay button and began the payment flow
      setButtonMessageKey('processing');
    } else {
      // Default state before the customer has clicked the pay button and began the payment flow
      setButtonMessageKey('pay');
    }
  }, [
    hasSucceededOrProcessingSetupAttempt,
    sessionState,
    isPaymentProcessing,
    isPollingPayment,
  ]);

  const buttonMessages = {
    pay: messages.payText,
    processing: messages.processingText,
    submitting: messages.submittingOnChainText,
    verifying: messages.verifyingTransactionText,
  };

  if (relayQuoteError) {
    return (
      <ScreenContentLayout
        onBack={() =>
          setValue((state) => {
            state.screen = 'TOKEN_SELECT';
          })
        }
      >
        <QuoteErrorScreen />
      </ScreenContentLayout>
    );
  }

  return (
    <>
      <ScreenContentLayout
        onBack={
          isAnyCryptoWalletConnectFlow
            ? () =>
                setValue((state) => {
                  state.screen = 'WALLET_SELECT_GRID';
                })
            : undefined
        }
        bottom={
          <>
            <BrandedButton
              disabled={
                isScreenInProgress ||
                insufficientBalance ||
                isSessionTerminal ||
                (isRelayCurrency && !relayQuote.depositAddress)
              }
              onClick={onClickPay}
              loading={isPaymentProcessing}
            >
              <AnimatePresence initial={false} mode="wait">
                <view.div
                  key={buttonMessageKey}
                  uses={[
                    motion({
                      initial: {opacity: 0},
                      animate: {opacity: 1},
                      exit: {opacity: 0},
                      transition: {duration: 0.25},
                    }),
                  ]}
                >
                  {buttonMessageKey === 'pay'
                    ? intl.formatMessage(messages.payText, {
                        amount:
                          isRelayCurrency && relayQuote.inputAmount
                            ? relayQuote.inputAmount
                            : // Ideally we should use localized amount. Fixed format for now. e.g., 1.00 USDC
                              formatCurrencyAmountToMajorUnitsFixed(
                                amount,
                                paymentCurrency,
                              ),
                        currency: formatAssetCode(
                          selectedCurrencyBalance.currency.assetCode,
                        ),
                      })
                    : intl.formatMessage(buttonMessages[buttonMessageKey])}
                </view.div>
              </AnimatePresence>
            </BrandedButton>
            {hasSucceededOrProcessingSetupAttempt ? (
              <TransactionProcessingTimeDisclaimer />
            ) : null}
            {setupFutureUsage && !isPollingPayment ? <SetupTerms /> : null}
            {explorerUrl ? (
              <LinkButton
                href={explorerUrl}
                css={{alignSelf: 'center'}}
                target="_blank"
              >
                {intl.formatMessage(messages.viewTransaction)}
              </LinkButton>
            ) : null}
          </>
        }
      >
        {enableNonStablesWalletUi ? (
          // Non-stables wallet UI. Available balance is still placeholder —
          // not the final business logic.
          <view.div css={{stack: 'y', gapY: 'medium'}}>
            <TokenCard
              assetCode={selectedCurrencyBalance.currency.assetCode}
              network={activeWallet.network}
              amount={
                isRelayCurrency && relayQuote.inputAmount
                  ? relayQuote.inputAmount
                  : formatCurrencyAmountToMajorUnitsFixed(
                      amount,
                      paymentCurrency,
                    )
              }
              refreshCountdown={
                secondsUntilRefresh !== null
                  ? {
                      secondsUntilRefresh,
                      totalSeconds: QUOTE_REFRESH_INTERVAL_S,
                    }
                  : null
              }
              loading={relayQuoteLoading}
            />
            <ConnectedWalletCard
              wallet={{...activeWallet, option: walletOption}}
              availableBalance={formatAmountToDollars(
                selectedCurrencyBalance.amountInUsdMinor ?? 0,
              )}
              actionMenu={
                <WalletActionMenu
                  disabled={isPaymentProcessing || isSessionTerminal}
                />
              }
            />
          </view.div>
        ) : (
          <WalletCard
            wallet={{
              ...activeWallet,
              option: walletOption,
            }}
            headerActions={
              <WalletActionMenu
                disabled={isPaymentProcessing || isSessionTerminal}
              />
            }
            bottomContent={
              <CurrencySelector
                selectedCurrencyId={selectedCurrency.id}
                onCurrencySelect={(id) => {
                  const currencyBalance = currencyBalances.find(
                    (c) => c.currency.id === id,
                  );
                  if (!currencyBalance) return;
                  setSelectedCurrency(currencyBalance.currency);
                }}
                label={intl.formatMessage(messages.currencyLabel)}
                currencyBalances={currencyBalances}
                disabled={isScreenInProgress || isSessionTerminal}
              />
            }
          />
        )}
        {insufficientBalance ? (
          <Banner
            message={intl.formatMessage(messages.insufficientBalance)}
            variant="error"
          />
        ) : null}
        <ErrorBanner />
        <SmartWalletBanner
          activeWallet={activeWallet}
          selectedCurrency={selectedCurrency}
        />
      </ScreenContentLayout>
      {showPaymentConfirmationPendingOverlay ? (
        <PaymentConfirmationPendingOverlay />
      ) : null}
      {showFutureUsageConfirmationPendingOverlay ? (
        <FutureUsageConfirmationPendingOverlay />
      ) : null}
    </>
  );
};
